import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import { useDispatch } from "react-redux";
import { fabric } from "fabric";
import styled from "styled-components";
import reactImageSize from "react-image-size";
import { onDispatchSrcAudio } from "edu_lms/modules/App/actions";
import { Image, Audio, AudioType } from "../../components";
import { AUDIO_ERROR, AUDIO_SUCCESS, URL_IMAGE_QUESTION } from "edu_lms/constants/type";
import { TYPE_GAME_MTC_BG, TYPE_CHECK } from "../../constants/MTC_BG";
import { ANSWER_COLOR } from "../../constants/styles";
import { checkResultAnswer, getAnswerColor, calculateImageDimensionToView } from "../../helpers/MTC_BG";

const MTC_BG = (
  {
    gameData,
    hideResultAnswer = false,
    selectedAnswersProp = [],
    showCorrectAnswer = false,
    isReadOnly = false,
	  showResult = false,
    onPlaying = () => {},
    onComplete = () => {},
  },
  ref
) => {
  const dispatch = useDispatch();
  const canvasId = `match-background_${Math.random()}`;

  const [imageDimension, setImageDimension] = useState({ width: 0, heigh: 0 });
  const [selectedPositionsAnswer, setSelectedPositionsAnswer] = useState([]);
  const [isCheckedAnswer, setIsCheckedAnswer] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const {
    answerCorrect,
    backgroundImage,
    checkType,
    fontSizeIcon,
    listAudioAnswer,
    touchAreas,
    typeGame,
  } = gameData;
  const correctAnswers = answerCorrect.split(",");

  useEffect(() => {
    if (selectedAnswersProp.length > 0) {
      setSelectedPositionsAnswer(selectedAnswersProp);
      setIsCheckedAnswer(true);
    }
  }, [selectedAnswersProp]);

  useImperativeHandle(ref, () => ({
    handleCheck: handleCheckAnswer,
    handleReset: handleResetAnswer,
    handleOnlyView,
  }));

  const handleCheckAnswer = () => {
    setIsCheckedAnswer(true);

    const isCorrect = checkResultAnswer(
      correctAnswers,
      selectedPositionsAnswer
    );
    !hideResultAnswer && dispatch(onDispatchSrcAudio(isCorrect ? AUDIO_SUCCESS : AUDIO_ERROR));

    onComplete({ selectedAnswers: selectedPositionsAnswer, isCorrect });
  };

  const handleResetAnswer = () => {
    setSelectedPositionsAnswer([]);
    setIsCheckedAnswer(false);
    setIsViewOnly(false);
  };

  const handleOnlyView = () => {
    setIsViewOnly(true);
  };

  const handleLoadImageBackground = async () => {
    const { width: realWidth, height: realHeight } = await reactImageSize(`${URL_IMAGE_QUESTION}${backgroundImage.src}`);
    const { width, height } = calculateImageDimensionToView(realWidth, realHeight);
    setImageDimension({ width, height });
  }

  useEffect(() => {
    drawCanvasTouchArea();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPositionsAnswer, imageDimension.width, imageDimension.heigh, isCheckedAnswer]);

  const drawCanvasTouchArea = () => {
    const newCanvas = new fabric.Canvas(canvasId, {
      width: imageDimension.width,
      height: imageDimension.height,
    });

    touchAreas.forEach((area) => {
      const { touchVectors } = area;
      const touchVectorsPolygon = touchVectors.map((vector) => {
        return {
          ...vector,
          x: vector.x * scaleImage,
          y: vector.y * scaleImage,
        };
      });

      const positionIndex = area.name.replace("object-", "");
	
      const polygon = new fabric.Polygon(touchVectorsPolygon, {
        strokeWidth: 1.5,
        hasBorders: true,
        selectionRadius: 20,
        stroke:
          checkType !== TYPE_CHECK.Tick
            ? getAnswerColor({
                correctAnswers,
                selectedPositionsAnswer,
                positionIndex,
                checkType,
                isCheckedAnswer,
                showCorrectAnswer,
                hideResultAnswer,
              })
            : ANSWER_COLOR.NONE,
        fill: ANSWER_COLOR.NONE,
        selectable: false,
        hoverCursor: "pointer",
        id: positionIndex,
      });
      newCanvas.add(polygon);
	 
	  if(showResult && correctAnswers.includes(positionIndex)) {
		  
		  const center = polygon.getCenterPoint(); // Lấy tâm của polygon
		  const text = new fabric.Text('Đúng', {
			  left: center.x,
			  top: center.y,
			  fontSize: 16,
			  fill: 'red',
			  originX: 'center',
			  originY: 'center',
			  selectable: false,
			  evented: false, // Không cho tương tác
		  });
		  
		  newCanvas.add(text);
	  }

    });
    newCanvas.renderAll();

    newCanvas.on("mouse:over", (e) => onMouseOver(e, newCanvas));

    newCanvas.on("mouse:out", (e) => onMouseOut(e, newCanvas));

    newCanvas.on("mouse:down", (e) => onMouseDown(e, newCanvas));
  };

  const onMouseOver = (e, canvas) => {
    if (!e.target) return;
    e.target.set("fill", "rgba(102, 217, 255, 0.2)");
    canvas.renderAll();
  };

  const onMouseOut = (e, canvas) => {
    if (!e.target) return;
    e.target.set("fill", "transparent");
    canvas.renderAll();
  };

  const onMouseDown = (e, canvas) => {
    if (!(e.target && e.button === 1)) return;
    if (isViewOnly) return;

    let shadowListPositionClick = [...selectedPositionsAnswer];
    if (isCheckedAnswer) {
      handleResetAnswer();
      shadowListPositionClick = [];
    }

    const positionOrder = e.target.get("id");
    if (typeGame === TYPE_GAME_MTC_BG.ManyAnswers) {
      const positionOrderFocusIndex = shadowListPositionClick.findIndex(position => position === positionOrder);
      if (positionOrderFocusIndex > -1) {
        shadowListPositionClick.splice(positionOrderFocusIndex, 1)
      } else {
        shadowListPositionClick.push(positionOrder);
      }
      setSelectedPositionsAnswer(shadowListPositionClick);
    } else {
      setSelectedPositionsAnswer([positionOrder]);
    }
    canvas.renderAll();
    
    onPlaying(false);
  };

  const getListCheckTypeTick = () => {
    const listCheckType = [];
    if (checkType !== TYPE_CHECK.Tick) return listCheckType;
    touchAreas.forEach((area) => {
      const { touchVectors } = area;
      const positionIndex = area.name.replace("object-", "");
      if (showCorrectAnswer || selectedPositionsAnswer.includes(positionIndex)) {
        listCheckType.push({
          left: touchVectors[3].x - 15 + (touchVectors[2].x - touchVectors[3].x) / 2, // magic number 15 ???
          top: touchVectors[2].y - 30, // magic number 30 ???
          color: getAnswerColor({
            correctAnswers,
            selectedPositionsAnswer,
            positionIndex,
            checkType,
            isCheckedAnswer,
            showCorrectAnswer,
            hideResultAnswer,
          }),
          id: positionIndex,
        });
      }
    });
    return listCheckType;
  };

  const scaleImage = imageDimension.width / backgroundImage.width;

  return (
    <PlayArea>
      <MultipleChoiceBackGroundContainerWrapper isView={isReadOnly || isViewOnly}>
        <Image
          src={backgroundImage.src}
          width={imageDimension.width}
          heigh={imageDimension.height}
          style={{ minWidth: imageDimension.width }}
          alt="MTC-background-image"
          onLoad={handleLoadImageBackground}
        />
        <canvas id={canvasId} />
	   
        {getListCheckTypeTick().map((check, index) => {
          return (
            <div
              key={index}
              style={{
                position: "absolute",
                zIndex: 1,
                top: check.top * scaleImage,
                left: check.left * scaleImage,
                color: check.color,
              }}
            >
              <IStyled
                fontSizeIcon={fontSizeIcon}
                className="fa fa-check"
                aria-hidden="true"
              />
            </div>
          );
        })}
	     
        {listAudioAnswer.map((audio, index) => {
          return (
            <div
              key={index}
              style={{
                position: "absolute",
                zIndex: 11,
                left: audio.left * scaleImage - 30, // magic number 30 ???
                top: audio.top * scaleImage - 20, // magic number 20 ???
              }}
            >
              <Audio variant={AudioType.Primary} src={audio.url} />
            </div>
          );
        })}
      </MultipleChoiceBackGroundContainerWrapper>
    </PlayArea>
  );
};

export default forwardRef(MTC_BG);

const IStyled = styled.i`
  font-size: ${(props) => props.fontSizeIcon};
`;

const MultipleChoiceBackGroundContainerWrapper = styled.div`
  pointer-events: ${props => props.isView ? 'none' : 'initial'};
  position: relative;
  width: fit-content;
  height: 100%;
  .answer-wrapper,
  .question-wrapper {
    z-index: 12;
    position: relative;
    .box-item {
      &:hover {
        background-color: rgba(102, 217, 255, 0.2) !important;
      }
    }
  }
  .canvas-container {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100% !important;
    z-index: 11;
  }
`;

const PlayArea = styled.div`
  display: flex;
  overflow: auto;
  justify-content: center;
`;
